import { lazy, Suspense } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import AppContext from "./AppContext.tsx";
import Layout from "./components/Layout/Layout.tsx";
import ProtectedRoute from "./components/ProtectedRoute";
import { PERMISSIONS } from "./constants/permissions";
import ErrorBoundary from "./components/ErrorBoundary";
import AssistantProvider from "./components/assistant/AssistantProvider";
import { ASSISTANT_ACCESS_PERMISSIONS } from "./lib/assistant-access";

const App = lazy(() => import("./pages/index.tsx"));
const Products = lazy(() => import("./pages/products/index.tsx"));
const AddProduct = lazy(() => import("./pages/products/add_edit.tsx"));
const AddBrand = lazy(() => import("./pages/brands/add_edit.tsx"));
const Brands = lazy(() => import("./pages/brands/index.tsx"));
const Categories = lazy(() => import("./pages/categories/index.tsx"));
const AddEditCategory = lazy(() => import("./pages/categories/add_edit.tsx"));
const Orders = lazy(() => import("./pages/orders/index.tsx"));
const AddOrder = lazy(() => import("./pages/orders/add_edit.tsx"));
const Users = lazy(() => import("./pages/users/index.tsx"));
const AddEditUser = lazy(() => import("./pages/users/add_edit.tsx"));
const ViewOrder = lazy(() => import("./pages/orders/view.tsx"));
const Roles = lazy(() => import("./pages/roles/index.tsx"));
const AddRole = lazy(() => import("./pages/roles/add_edit.tsx"));
const Coupons = lazy(() => import("./pages/coupons/index.tsx"));
const AddEditCoupon = lazy(() => import("./pages/coupons/add_edit.tsx"));
const Attributes = lazy(() => import("./pages/attributes/index.tsx"));
const AddAttribute = lazy(() => import("./pages/attributes/add_edit.tsx"));
const PCGen = lazy(() => import("./pages/pcgen/index.tsx"));
const Reviews = lazy(() => import("./pages/reviews/index.tsx"));
const OrderReviews = lazy(() => import("./pages/orderReviews/index.tsx"));
const ProductRedirect = lazy(() => import("./pages/ProductRedirect.tsx"));
const AuditLogs = lazy(() => import("./pages/auditlogs/index.tsx"));
const Banners = lazy(() => import("./pages/banners/index.tsx"));
const UpgradeTypes = lazy(() => import("./pages/upgradeTypes/index.tsx"));
const AddUpgradeType = lazy(() => import("./pages/upgradeTypes/add_edit.tsx"));
const ManageUpgradeConfiguration = lazy(
  () => import("./pages/upgradeTypes/manage.tsx"),
);
const UpgradeTemplates = lazy(
  () => import("./pages/upgradeTypes/templates.tsx"),
);
const Stats = lazy(() => import("./pages/stats.tsx"));
const Maintenance = lazy(() => import("./pages/maintenance/index.tsx"));
const ViewMaintenance = lazy(() => import("./pages/maintenance/view.tsx"));
const CreateMaintenance = lazy(() => import("./pages/maintenance/create.tsx"));
const McpAccess = lazy(() => import("./pages/settings/mcp.tsx"));

const routeFallback = (
  <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center text-sm text-muted-foreground">
    Loading...
  </div>
);

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <Layout>
        <App />
      </Layout>
    ),
  },
  {
    path: "/stats",
    element: (
      <Layout>
        <Stats />
      </Layout>
    ),
  },
  {
    path: "/products",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_VIEW}>
        <Products />
      </ProtectedRoute>
    ),
  },
  {
    path: "/orders",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.ORDERS_VIEW}>
        <Orders />
      </ProtectedRoute>
    ),
  },
  {
    path: "/orders/add",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.ORDERS_VIEW}>
        <AddOrder />
      </ProtectedRoute>
    ),
  },
  {
    path: "/orders/edit/:id",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.ORDERS_VIEW}>
        <AddOrder />
      </ProtectedRoute>
    ),
  },
  {
    path: "/orders/:id",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.ORDERS_VIEW}>
        <ViewOrder />
      </ProtectedRoute>
    ),
  },
  {
    path: "/products/add",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_EDIT}>
        <AddProduct />
      </ProtectedRoute>
    ),
  },
  {
    path: "/products/:id",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_VIEW}>
        <AddProduct />
      </ProtectedRoute>
    ),
  },
  {
    path: "/brands",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_VIEW}>
        <Brands />
      </ProtectedRoute>
    ),
  },
  {
    path: "/brands/add",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_EDIT}>
        <AddBrand />
      </ProtectedRoute>
    ),
  },
  {
    path: "/brands/edit/:id",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_EDIT}>
        <AddBrand />
      </ProtectedRoute>
    ),
  },
  {
    path: "/categories",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_VIEW}>
        <Categories />
      </ProtectedRoute>
    ),
  },
  {
    path: "/categories/add",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_EDIT}>
        <AddEditCategory />
      </ProtectedRoute>
    ),
  },
  {
    path: "/categories/edit/:id",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_EDIT}>
        <AddEditCategory />
      </ProtectedRoute>
    ),
  },
  {
    path: "/attributes",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_VIEW}>
        <Attributes />
      </ProtectedRoute>
    ),
  },
  {
    path: "/attributes/add",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_EDIT}>
        <AddAttribute />
      </ProtectedRoute>
    ),
  },
  {
    path: "/attributes/edit/:id",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_EDIT}>
        <AddAttribute />
      </ProtectedRoute>
    ),
  },
  {
    path: "/users",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.USERS_VIEW}>
        <Users />
      </ProtectedRoute>
    ),
  },
  {
    path: "/users/add",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.USERS_EDIT}>
        <AddEditUser />
      </ProtectedRoute>
    ),
  },
  {
    path: "/users/edit/:id",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.USERS_EDIT}>
        <AddEditUser />
      </ProtectedRoute>
    ),
  },
  {
    path: "/roles",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.ADMIN}>
        <Roles />
      </ProtectedRoute>
    ),
  },
  {
    path: "/roles/add",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.ADMIN}>
        <AddRole />
      </ProtectedRoute>
    ),
  },
  {
    path: "/roles/edit/:id",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.ADMIN}>
        <AddRole />
      </ProtectedRoute>
    ),
  },
  {
    path: "/coupons",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.COUPONS_VIEW}>
        <Coupons />
      </ProtectedRoute>
    ),
  },
  {
    path: "/coupons/add",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.COUPONS_EDIT}>
        <AddEditCoupon />
      </ProtectedRoute>
    ),
  },
  {
    path: "/coupons/:id",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.COUPONS_EDIT}>
        <AddEditCoupon />
      </ProtectedRoute>
    ),
  },
  {
    path: "/PCGen",
    element: <PCGen />,
  },
  {
    path: "/product-reviews",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.ORDERS_VIEW}>
        <Reviews />
      </ProtectedRoute>
    ),
  },
  {
    path: "/order-reviews",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.ORDERS_VIEW}>
        <OrderReviews />
      </ProtectedRoute>
    ),
  },
  {
    path: "/:lang/:category/:slug/:color?",
    element: <ProductRedirect />,
  },
  {
    path: "/auditLogs",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.USERS_VIEW}>
        <AuditLogs />
      </ProtectedRoute>
    ),
  },
  {
    path: "/banners",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_EDIT}>
        <Banners />
      </ProtectedRoute>
    ),
  },
  {
    path: "/types",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_EDIT}>
        <UpgradeTypes />
      </ProtectedRoute>
    ),
  },
  {
    path: "/types/manage/:variantId",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_EDIT}>
        <ManageUpgradeConfiguration />
      </ProtectedRoute>
    ),
  },
  {
    path: "/upgrade-templates",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_EDIT}>
        <UpgradeTemplates />
      </ProtectedRoute>
    ),
  },
  {
    path: "/types/add",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_EDIT}>
        <AddUpgradeType />
      </ProtectedRoute>
    ),
  },
  {
    path: "/types/edit/:id",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.PRODUCTS_EDIT}>
        <AddUpgradeType />
      </ProtectedRoute>
    ),
  },
  {
    path: "/maintenance",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.ORDERS_VIEW}>
        <Maintenance />
      </ProtectedRoute>
    ),
  },
  {
    path: "/maintenance/new",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.ORDERS_MANAGE}>
        <CreateMaintenance />
      </ProtectedRoute>
    ),
  },
  {
    path: "/maintenance/:id",
    element: (
      <ProtectedRoute requiredPermission={PERMISSIONS.ORDERS_VIEW}>
        <ViewMaintenance />
      </ProtectedRoute>
    ),
  },
  {
    path: "/settings/mcp",
    element: (
      <ProtectedRoute anyOfPermissions={ASSISTANT_ACCESS_PERMISSIONS}>
        <McpAccess />
      </ProtectedRoute>
    ),
  },
]);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <AppContext>
      <AssistantProvider>
        <Suspense fallback={routeFallback}>
          <RouterProvider router={router} />
        </Suspense>
      </AssistantProvider>
    </AppContext>
  </ErrorBoundary>,
);
